/** `sections/card-grid.liquid` — `<card-grid>`: static multi-column grid on desktop
 *  that becomes a horizontal ScrollBooster drag carousel on mobile (≤768px), with a
 *  shared progress bar and card-by-card prev/next arrows (Figma 6386:13225).
 *
 *  The booster is only instantiated while the mobile media query matches; on desktop it
 *  is destroyed and the track transform is cleared so the CSS grid lays out normally.
 */

import ScrollBooster from 'scrollbooster'
import { CarouselPagination } from './carousel-pagination.js'

const MOBILE_QUERY = '(max-width: 768px)'

const VIEWPORT_SELECTOR = '[data-card-grid-viewport]'
const TRACK_SELECTOR = '[data-card-grid-track]'
const CARD_SELECTOR = '[data-card-grid-card]'
const PREV_SELECTOR = '[data-card-grid-prev]'
const NEXT_SELECTOR = '[data-card-grid-next]'
const PAGINATION_SELECTOR = '[data-card-grid-pagination]'
const GRAB_CLASS = 'is-grabbing'

class CardGrid extends HTMLElement {
  #abort
  #booster
  #viewport
  #track
  #prevBtn
  #nextBtn
  #pagination
  #resizeObserver
  #mql

  connectedCallback() {
    this.#viewport = this.querySelector(VIEWPORT_SELECTOR)
    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#prevBtn = this.querySelector(PREV_SELECTOR)
    this.#nextBtn = this.querySelector(NEXT_SELECTOR)
    if (!this.#viewport || !this.#track) return

    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#pagination = new CarouselPagination(this.querySelector(PAGINATION_SELECTOR), {
      signal,
      onSelect: (x) => this.#booster?.scrollTo({ x }),
      autoplay: true,
      pauseOn: this,
    })

    this.#prevBtn?.addEventListener('click', this.#onPrev, { signal })
    this.#nextBtn?.addEventListener('click', this.#onNext, { signal })

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#refresh())
      this.#resizeObserver.observe(this.#viewport)
      this.#resizeObserver.observe(this.#track)
    }

    this.#mql = globalThis.matchMedia?.(MOBILE_QUERY)
    this.#mql?.addEventListener('change', this.#applyMode, { signal })
    this.#applyMode()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#resizeObserver?.disconnect()
    this.#pagination?.destroy()
    this.#destroyBooster()
  }

  get #isMobile() {
    return Boolean(this.#mql?.matches)
  }

  #applyMode = () => {
    if (this.#isMobile) {
      this.#ensureBooster()
      this.#refresh()
    } else {
      this.#destroyBooster()
    }
  }

  #ensureBooster() {
    if (this.#booster) return
    this.#booster = new ScrollBooster({
      viewport: this.#viewport,
      content: this.#track,
      scrollMode: 'transform',
      direction: 'horizontal',
      pointerMode: 'touch',
      emulateScroll: true,
      preventDefaultOnEmulateScroll: 'horizontal',
      lockScrollOnDragDirection: 'horizontal',
      bounce: true,
      bounceForce: 0.1,
      textSelection: true,
      onPointerDown: () => this.#viewport.classList.add(GRAB_CLASS),
      onPointerUp: () => this.#viewport.classList.remove(GRAB_CLASS),
      onUpdate: (state) => this.#syncArrowState(state),
    })
  }

  #destroyBooster() {
    if (!this.#booster) return
    this.#booster.destroy()
    this.#booster = undefined
    this.#viewport?.classList.remove(GRAB_CLASS)
    if (this.#track) this.#track.style.transform = ''
  }

  #refresh() {
    if (!this.#isMobile) return
    this.#booster?.updateMetrics()
    this.#syncArrowState()
  }

  #onPrev = () => this.#scrollByCards(-1)
  #onNext = () => this.#scrollByCards(1)

  #scrollByCards(direction) {
    if (!this.#booster) return
    const step = this.#cardStep()
    const current = this.#booster.getState().position.x
    const max = this.#maxScroll()
    const target = Math.max(0, Math.min(max, current + direction * step))
    this.#booster.scrollTo({ x: target })
  }

  #cardStep() {
    const cards = this.#track?.querySelectorAll(CARD_SELECTOR)
    if (!cards || cards.length === 0) return this.#viewport.clientWidth
    const first = cards[0]
    const second = cards[1]
    if (second) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left
    }
    const style = globalThis.getComputedStyle(this.#track)
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0
    return first.getBoundingClientRect().width + gap
  }

  #maxScroll() {
    if (!this.#viewport || !this.#track) return 0
    return Math.max(0, this.#track.scrollWidth - this.#viewport.clientWidth)
  }

  #syncArrowState(state) {
    const max = this.#maxScroll()
    const hasOverflow = max > 1
    const current = this.#booster ? (state ?? this.#booster.getState()).position.x : 0
    const atStart = current <= 1
    const atEnd = current >= max - 1

    if (this.#prevBtn) this.#prevBtn.disabled = !hasOverflow || atStart
    if (this.#nextBtn) this.#nextBtn.disabled = !hasOverflow || atEnd

    this.#pagination?.update({ current, max, step: this.#cardStep() })
  }
}

if (!customElements.get('card-grid')) {
  customElements.define('card-grid', CardGrid)
}
